import { compareDecimalStrings } from "@/shared/utils/decimal";

import {
  PaymentDirection,
  type CorridorCoverage,
  type PaymentFeeRail,
  type ProviderRailRoute,
} from "../types/paymentFees.types";
import type { PaymentTax } from "../types/paymentTaxes.types";
import { clientTotal } from "./feeMath";
import { applyTaxes, resolveTaxWinners, taxScopeKey, type AppliedTaxes, type TaxScope } from "./taxMath";

export type TaxSource = "OWN" | "GENERAL";

export interface TaxCorridorLine {
  /** The rule in force on this corridor, in the page's scope. */
  tax: PaymentTax;
  source: TaxSource;
  exempt: boolean;
  /** At the corridor's example amount; null when exempt or the commission it is on is unknown. */
  amount: string | null;
  /** Organization scope: what the corridor charges without the organization's own rule. */
  general: PaymentTax | null;
}

export interface TaxOrgOverride {
  organizationId: string;
  lines: PaymentTax[];
}

export interface TaxCorridorCoverage {
  corridor: CorridorCoverage;
  lines: TaxCorridorLine[];
  clientTotal: string;
  colocaTotal: string;
  /** General scope only: organizations with their own rule on this corridor. */
  overrides: TaxOrgOverride[];
  /** Organization scope only: the organization has its own rule here. */
  hasOwn: boolean;
  hasScheduledChange: boolean;
}

export type TaxCorridorFilter = "all" | "taxed" | "own" | "scheduled";

export interface TaxScopeChoice {
  rail: PaymentFeeRail | null;
  direction: PaymentDirection | null;
}

const isExempt = (tax: PaymentTax) => compareDecimalStrings(tax.rate, "0") === 0;
const startsAt = (tax: PaymentTax) => new Date(tax.valid_from).getTime();

export function scopeOfRoute(route: ProviderRailRoute, organizationId: string | null): TaxScope {
  return {
    country: route.country,
    rail: route.rail,
    direction: route.direction,
    currency: route.currency,
    organizationId,
  };
}

export function sameTaxScope(a: TaxScopeChoice, b: TaxScopeChoice): boolean {
  return (a.rail ?? null) === (b.rail ?? null) && (a.direction ?? null) === (b.direction ?? null);
}

/** Whether a row can price an operation of this corridor, for this organization (null = the general view). */
export function taxReaches(
  tax: PaymentTax,
  route: ProviderRailRoute,
  organizationId: string | null,
): boolean {
  return (
    tax.country === route.country &&
    (!tax.rail || tax.rail === route.rail) &&
    (!tax.direction || tax.direction === route.direction) &&
    (!tax.organization_id || tax.organization_id === organizationId)
  );
}

/** The taxes of one corridor at its example amount, priced as the backend prices the operation. */
export function corridorTaxAmounts(corridor: CorridorCoverage, taxes: PaymentTax[]): AppliedTaxes {
  const { route } = corridor;
  return applyTaxes(taxes, {
    grossAmount: corridor.exampleAmount,
    commission: corridor.commissionAmount ?? "0",
    currency: route.currency,
    ...(route.direction === PaymentDirection.PAYOUT
      ? {
          payoutFees:
            clientTotal(corridor.commissionAmount, corridor.costAmount, route.economic_model) ?? "0",
        }
      : {}),
  });
}

function overridesOn(route: ProviderRailRoute, rows: PaymentTax[], now: number): TaxOrgOverride[] {
  const organizationIds = [
    ...new Set(
      rows
        .filter((tax) => tax.organization_id && tax.country === route.country)
        .map((tax) => tax.organization_id as string),
    ),
  ];
  return organizationIds
    .map((organizationId) => ({
      organizationId,
      lines: resolveTaxWinners(rows, scopeOfRoute(route, organizationId), now).filter(
        (tax) => tax.organization_id === organizationId,
      ),
    }))
    .filter((override) => override.lines.length > 0);
}

export function buildTaxCoverage(
  corridors: CorridorCoverage[],
  rows: PaymentTax[],
  now: number,
  organizationId: string | null,
): TaxCorridorCoverage[] {
  return corridors.map((corridor) => {
    const { route } = corridor;
    const winners = resolveTaxWinners(rows, scopeOfRoute(route, organizationId), now);
    const generalWinners = organizationId ? resolveTaxWinners(rows, scopeOfRoute(route, null), now) : [];
    const applied = corridorTaxAmounts(
      corridor,
      winners.filter((tax) => !isExempt(tax)),
    );
    const amountOf = new Map(applied.lines.map((line) => [line.tax.id, line.amount]));
    const lines = winners.map<TaxCorridorLine>((tax) => ({
      tax,
      source: tax.organization_id ? "OWN" : "GENERAL",
      exempt: isExempt(tax),
      amount:
        isExempt(tax) || (tax.base === "COMMISSION" && corridor.commissionAmount == null)
          ? null
          : (amountOf.get(tax.id) ?? "0"),
      general: organizationId ? (generalWinners.find((other) => other.code === tax.code) ?? null) : null,
    }));
    return {
      corridor,
      lines,
      clientTotal: applied.clientTotal,
      colocaTotal: applied.colocaTotal,
      overrides: organizationId ? [] : overridesOn(route, rows, now),
      hasOwn: lines.some((line) => line.source === "OWN"),
      hasScheduledChange: rows.some((tax) => startsAt(tax) > now && taxReaches(tax, route, organizationId)),
    };
  });
}

export function matchesTaxFilter(
  coverage: TaxCorridorCoverage,
  filter: TaxCorridorFilter,
  scoped: boolean,
): boolean {
  switch (filter) {
    case "taxed":
      return coverage.lines.some((line) => !line.exempt);
    case "own":
      return scoped ? coverage.hasOwn : coverage.overrides.length > 0;
    case "scheduled":
      return coverage.hasScheduledChange;
    default:
      return true;
  }
}

/* ------------------------------------------- editing ------------------------------------------- */

export interface TaxEditTarget {
  /** The rule this corridor follows today in the scope written: the organization's own, or the general one. */
  effective: PaymentTax | null;
  corridorScope: TaxScopeChoice;
  /** A wider scope the change can be written to instead, and the corridors it would reach. */
  broad: { scope: TaxScopeChoice; routes: ProviderRailRoute[] } | null;
}

/** A row as it would be saved, to replay resolution with it. */
export function candidateTax(
  base: PaymentTax,
  patch: Pick<PaymentTax, "rate" | "bearer" | "organization_id" | "rail" | "direction" | "valid_from">,
): PaymentTax {
  return { ...base, ...patch, id: "candidate", version: base.version + 1 };
}

/** Corridors of the country where `candidate` would be the rule of its code. */
export function routesWonBy(
  candidate: PaymentTax,
  rows: PaymentTax[],
  routes: ProviderRailRoute[],
  now: number,
): ProviderRailRoute[] {
  const at = Math.max(now, startsAt(candidate));
  const withCandidate = [...rows, candidate];
  return routes.filter(
    (route) =>
      taxReaches(candidate, route, candidate.organization_id) &&
      resolveTaxWinners(withCandidate, scopeOfRoute(route, candidate.organization_id), at).some(
        (tax) => tax.id === candidate.id,
      ),
  );
}

export function taxEditTarget(
  route: ProviderRailRoute,
  code: string,
  organizationId: string | null,
  rows: PaymentTax[],
  routes: ProviderRailRoute[],
  now: number,
): TaxEditTarget {
  const winner =
    resolveTaxWinners(rows, scopeOfRoute(route, organizationId), now).find((tax) => tax.code === code) ??
    null;
  const effective = organizationId ? (winner?.organization_id === organizationId ? winner : null) : winner;
  const corridorScope: TaxScopeChoice = { rail: route.rail, direction: route.direction };
  const broadScope: TaxScopeChoice | null = effective
    ? sameTaxScope(effective, corridorScope)
      ? null
      : { rail: effective.rail, direction: effective.direction }
    : { rail: null, direction: null };
  if (!broadScope) return { effective, corridorScope, broad: null };

  const template =
    effective ?? winner ?? rows.find((tax) => tax.code === code && tax.country === route.country);
  const covered = template
    ? routesWonBy(
        candidateTax(template, {
          rate: template.rate,
          bearer: template.bearer,
          organization_id: organizationId,
          ...broadScope,
          valid_from: new Date(now).toISOString(),
        }),
        rows,
        routes.filter((other) => other.country === route.country),
        now,
      )
    : [];
  return { effective, corridorScope, broad: { scope: broadScope, routes: covered } };
}

/* ------------------------------------------- removal ------------------------------------------- */

export interface OwnTaxRemovalPlan {
  /** Deletion order: in every scope, the version in force goes last. */
  rows: PaymentTax[];
  /** Corridors where the removed rules apply today, with what they charge afterwards. */
  impact: { route: ProviderRailRoute; after: PaymentTax[] }[];
}

/** Every own rule the organization pays on this corridor, every version of each. */
export function ownTaxRemovalPlan(
  route: ProviderRailRoute,
  organizationId: string,
  rows: PaymentTax[],
  routes: ProviderRailRoute[],
  now: number,
): OwnTaxRemovalPlan | null {
  const owned = resolveTaxWinners(rows, scopeOfRoute(route, organizationId), now).filter(
    (tax) => tax.organization_id === organizationId,
  );
  if (owned.length === 0) return null;

  const keys = new Set(owned.map(taxScopeKey));
  const ordered = owned.flatMap((winner) => [
    ...rows
      .filter((tax) => taxScopeKey(tax) === taxScopeKey(winner) && tax.id !== winner.id)
      .sort((a, b) => startsAt(b) - startsAt(a)),
    winner,
  ]);
  const remaining = rows.filter((tax) => !keys.has(taxScopeKey(tax)));
  const codes = new Set(owned.map((tax) => tax.code));
  const impact = routes
    .filter((other) =>
      resolveTaxWinners(rows, scopeOfRoute(other, organizationId), now).some((tax) =>
        keys.has(taxScopeKey(tax)),
      ),
    )
    .map((other) => ({
      route: other,
      after: resolveTaxWinners(remaining, scopeOfRoute(other, organizationId), now).filter((tax) =>
        codes.has(tax.code),
      ),
    }));
  return { rows: ordered, impact };
}

/* ------------------------------------------- history ------------------------------------------- */

export type TaxChangeStatus = "ACTIVE" | "SCHEDULED" | "SUPERSEDED";

export interface TaxChange {
  tax: PaymentTax;
  previous: PaymentTax | null;
  status: TaxChangeStatus;
  /** The next version's start: a row is never closed. */
  effectiveUntil: string | null;
  source: TaxSource;
}

/** Every version of every scope, newest change first. */
export function taxChanges(rows: PaymentTax[], now: number): TaxChange[] {
  const byScope = new Map<string, PaymentTax[]>();
  for (const tax of rows) byScope.set(taxScopeKey(tax), [...(byScope.get(taxScopeKey(tax)) ?? []), tax]);
  const changes = [...byScope.values()].flatMap((versions) => {
    const ascending = [...versions].sort((a, b) => startsAt(a) - startsAt(b) || a.version - b.version);
    return ascending.map<TaxChange>((tax, index) => {
      const next = ascending[index + 1] ?? null;
      return {
        tax,
        previous: ascending[index - 1] ?? null,
        status: startsAt(tax) > now ? "SCHEDULED" : next && startsAt(next) <= now ? "SUPERSEDED" : "ACTIVE",
        effectiveUntil: next?.valid_from ?? null,
        source: tax.organization_id ? "OWN" : "GENERAL",
      };
    });
  });
  return changes.sort((a, b) => new Date(b.tax.created_at).getTime() - new Date(a.tax.created_at).getTime());
}

/** The changes that reach one corridor in the page's scope. */
export function corridorTaxChanges(
  route: ProviderRailRoute,
  organizationId: string | null,
  rows: PaymentTax[],
  now: number,
): TaxChange[] {
  return taxChanges(
    rows.filter((tax) => taxReaches(tax, route, organizationId)),
    now,
  );
}
